import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Eye,
  FileText,
  Info,
  Loader2,
  Search,
  ShieldCheck,
  Stethoscope,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Admin Render-Preview — paste-or-fetch markdown, see the rendered PDF
 * inline.
 *
 * Two modes:
 *  • Paste mode (default) — drop raw `content_markdown` into the
 *    textarea, pick a render target (student sheet or lesson plan),
 *    optionally tweak sheet_type/title/grade, click Render. The PDF
 *    appears in the iframe on the right.
 *  • Lookup mode — paste an activity ID, click Fetch. The component
 *    pulls every sheet's raw markdown from the activity and shows
 *    the list; pick a sheet to load its markdown into the textarea,
 *    or use "Lesson markdown" to load the full teacher-facing
 *    lesson body.
 *
 * The actual rendering happens on the backend through the same
 * `build_student_sheets_pdf` / `build_pdf` calls a teacher's download
 * uses — so what you see here is what they see.
 */
const SAMPLE_MARKDOWN = `## Part 1 — Plan Your Design

1. Sketch two different design ideas. Think about:
- Where will the wheels go?
- How will they spin?

- Design Idea 1:
- Design Idea 2:

## Part 2 — Reflect

2. Why did one design work better than the other?

3. In your opinion, what would you change next time?

## Words to Know

- **Prototype**
- **Iteration**:
`;

const SHEET_TYPES = [
  "worksheet",
  "graphic_organizer",
  "blueprint",
  "tracker",
  "reflection",
  "exit_ticket",
  "reading_handout",
  "reference_card",
];

const RenderPreview = () => {
  const { user, loading: authLoading } = useAuth();
  const [renderAs, setRenderAs] = useState("student_sheet");
  const [sheetType, setSheetType] = useState("worksheet");
  const [sheetTitle, setSheetTitle] = useState("Render Preview");
  const [activityName, setActivityName] = useState("Admin Render Preview");
  const [grade, setGrade] = useState("4");
  const [markdown, setMarkdown] = useState(SAMPLE_MARKDOWN);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [lookupId, setLookupId] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [recent, setRecent] = useState([]);
  const [recentQuery, setRecentQuery] = useState("");
  const [loadingRecent, setLoadingRecent] = useState(false);

  // Phase 124.V — Layout Linter state.
  const [linting, setLinting] = useState(false);
  const [lintReport, setLintReport] = useState(null);
  const [lintSurface, setLintSurface] = useState("lesson_plan");
  const [activeTab, setActiveTab] = useState("render");

  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}/api`;
  }, []);

  const fetchRecent = async (query = "") => {
    setLoadingRecent(true);
    try {
      const params = new URLSearchParams({ limit: "30" });
      if (query.trim()) params.set("q", query.trim());
      const res = await fetch(`${apiBase}/admin/render-preview/recent?${params}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Recent fetch failed (${res.status})`);
      const data = await res.json();
      setRecent(data.lessons || []);
    } catch (e) {
      toast.error(e?.message || "Couldn't load recent lessons.");
    } finally {
      setLoadingRecent(false);
    }
  };

  useEffect(() => {
    if (!user || !user.is_admin) return;
    fetchRecent("");
  }, [user]);

  useEffect(() => {
    return () => {
      // Revoke the blob URL when the component unmounts so we don't
      // leak a few MB of PDF memory each navigation.
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] grid place-items-center text-[#4a6984]/70">
        Checking access…
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] text-[#4a6984] grid place-items-center">
        <div className="ae-card p-10 max-w-md text-center" data-testid="render-preview-forbidden">
          <ShieldCheck className="h-6 w-6 mx-auto text-[#1c2d3d] mb-3" />
          <h2 className="font-extrabold text-xl">Admin only</h2>
          <p className="mt-2 text-[#4a6984]/80">
            This area is restricted. Head back to your dashboard.
          </p>
          <Link
            to="/dashboard"
            className="ae-cta mt-6 inline-flex items-center px-6 py-3 rounded-lg text-sm"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  const handleRender = async () => {
    if (!markdown.trim()) {
      toast.error("Paste some markdown first.");
      return;
    }
    setRendering(true);
    try {
      const res = await fetch(`${apiBase}/admin/render-preview`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          markdown,
          render_as: renderAs,
          sheet_type: sheetType,
          sheet_title: sheetTitle,
          activity_name: activityName,
          grade,
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Render failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      toast.success("Rendered.");
    } catch (e) {
      toast.error(e?.message || "Render failed.");
    } finally {
      setRendering(false);
    }
  };

  const handleLookup = async (overrideId) => {
    const id = (overrideId ?? lookupId).trim();
    if (!id) {
      toast.error("Enter or pick an activity.");
      return;
    }
    setLookingUp(true);
    setLookupResult(null);
    try {
      const res = await fetch(
        `${apiBase}/admin/render-preview/lookup/${encodeURIComponent(id)}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Lookup failed (${res.status})`);
      }
      const data = await res.json();
      setLookupResult(data);
      if (data.activity_name) setActivityName(data.activity_name);
      if (data.grade) setGrade(String(data.grade));
      toast.success(`Loaded "${data.activity_name}"`);
    } catch (e) {
      toast.error(e?.message || "Lookup failed.");
    } finally {
      setLookingUp(false);
    }
  };

  const pickRecent = (id) => {
    setLookupId(id);
    handleLookup(id);
  };

  const loadSheet = (idx) => {
    if (!lookupResult) return;
    const md = lookupResult.sheet_markdowns?.[idx] || "";
    const sheet = lookupResult.student_sheets?.[idx];
    setMarkdown(md);
    if (sheet) {
      setSheetTitle(sheet.title);
      setSheetType(sheet.sheet_type);
    }
    setRenderAs("student_sheet");
    toast.success(`Loaded sheet: ${sheet?.title || idx}`);
  };

  const loadLesson = () => {
    if (!lookupResult?.lesson_markdown) {
      toast.error("This activity has no lesson markdown stored.");
      return;
    }
    setMarkdown(lookupResult.lesson_markdown);
    setRenderAs("lesson_plan");
    toast.success("Loaded lesson markdown.");
  };

  /**
   * Phase 124.V — Layout Linter.
   * Renders the picked activity to PDF on the backend and parses
   * the content stream for mint-tint rect anomalies (cards near the
   * 110mm cap, pages with >45% mint coverage, etc). Surfaces
   * findings inline so the admin can catch "big green block"
   * regressions before any teacher reports one.
   */
  const runLint = async () => {
    if (!lookupResult) {
      toast.error("Pick a lesson first.");
      return;
    }
    setLinting(true);
    setLintReport(null);
    try {
      const res = await fetch(
        `${apiBase}/admin/render-preview/lint/${encodeURIComponent(
          lookupResult.activity_id,
        )}?surface=${lintSurface}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Lint failed (${res.status})`);
      }
      const data = await res.json();
      setLintReport(data);
      if ((data.findings || []).length === 0) {
        toast.success(`Lint clean across ${data.total_pages} pages.`);
      } else {
        toast.warning(`${data.findings.length} finding(s) — see report.`);
      }
    } catch (e) {
      toast.error(e?.message || "Lint failed.");
    } finally {
      setLinting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f5f7] text-[#1c2d3d]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center text-xs font-extrabold uppercase tracking-wide text-[#4a6984] hover:text-[#1c2d3d]"
              data-testid="render-preview-back"
            >
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Admin
            </Link>
            <h1 className="font-extrabold text-3xl mt-2 flex items-center gap-2">
              <Eye className="h-7 w-7 text-[#96ded1]" /> Render Preview
            </h1>
            <p className="text-sm text-[#4a6984]/80 mt-1">
              Paste raw markdown — or look up a teacher&apos;s activity — and see
              the rendered PDF inline. Same code path as a real teacher download.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left column — inputs */}
          <div className="space-y-4">
            <section className="ae-card p-5">
              <h2 className="text-xs font-extrabold uppercase tracking-wide text-[#4a6984] mb-3 flex items-center gap-1.5">
                <Search className="h-3.5 w-3.5" /> Pick a lesson
              </h2>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={recentQuery}
                  onChange={(e) => setRecentQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") fetchRecent(recentQuery); }}
                  placeholder="Filter by lesson name (e.g. marshmallow)"
                  data-testid="render-preview-recent-filter"
                  className="flex-1 px-3 py-2 rounded-md border border-[#4a6984]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
                />
                <button
                  type="button"
                  onClick={() => fetchRecent(recentQuery)}
                  disabled={loadingRecent}
                  data-testid="render-preview-recent-search"
                  className="ae-cta inline-flex items-center px-4 py-2 rounded-md text-sm font-bold"
                >
                  {loadingRecent ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Search"}
                </button>
              </div>
              <div className="max-h-[260px] overflow-y-auto border border-[#4a6984]/10 rounded-md divide-y divide-[#4a6984]/10">
                {loadingRecent ? (
                  <div className="px-3 py-4 text-xs text-[#4a6984]/60">Loading…</div>
                ) : recent.length === 0 ? (
                  <div className="px-3 py-4 text-xs text-[#4a6984]/60">No lessons found.</div>
                ) : (
                  recent.map((row) => (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => pickRecent(row.id)}
                      data-testid={`render-preview-pick-${row.id}`}
                      className="block w-full text-left px-3 py-2 hover:bg-[#96ded1]/15 transition-colors"
                    >
                      <div className="text-sm font-bold text-[#1c2d3d] truncate">
                        {row.activity_name}
                      </div>
                      <div className="text-[10px] text-[#4a6984]/70 mt-0.5 flex flex-wrap gap-x-2">
                        {row.grade ? <span>Grade {row.grade}</span> : null}
                        {row.subject ? <span>· {row.subject}</span> : null}
                        {row.sheet_count ? <span>· {row.sheet_count} sheet{row.sheet_count === 1 ? "" : "s"}</span> : null}
                        {row.owner_email ? <span>· {row.owner_email}</span> : null}
                      </div>
                    </button>
                  ))
                )}
              </div>
              <details className="mt-3 text-xs text-[#4a6984]/70">
                <summary className="cursor-pointer">Or paste an activity ID…</summary>
                <div className="flex gap-2 mt-2">
                  <input
                    type="text"
                    value={lookupId}
                    onChange={(e) => setLookupId(e.target.value)}
                    placeholder="Activity ID"
                    data-testid="render-preview-lookup-input"
                    className="flex-1 px-3 py-2 rounded-md border border-[#4a6984]/20 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleLookup()}
                    disabled={lookingUp}
                    data-testid="render-preview-lookup-button"
                    className="ae-cta inline-flex items-center px-4 py-2 rounded-md text-sm font-bold"
                  >
                    {lookingUp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Fetch"}
                  </button>
                </div>
              </details>
              {lookupResult ? (
                <div className="mt-3 text-sm border-t border-[#4a6984]/10 pt-3">
                  <div className="font-bold">{lookupResult.activity_name}</div>
                  {lookupResult.owner_email ? (
                    <div className="text-[#4a6984]/70 text-xs">
                      Owner: {lookupResult.owner_email}
                    </div>
                  ) : null}
                  {lookupResult.grade ? (
                    <div className="text-[#4a6984]/70 text-xs">
                      Grade: {lookupResult.grade}
                    </div>
                  ) : null}
                  <div className="mt-2 space-y-1">
                    {lookupResult.lesson_markdown ? (
                      <button
                        type="button"
                        onClick={loadLesson}
                        data-testid="render-preview-load-lesson"
                        className="block w-full text-left px-2 py-1.5 rounded hover:bg-[#96ded1]/20 text-xs"
                      >
                        <FileText className="h-3 w-3 inline mr-1" />
                        Lesson markdown ({lookupResult.lesson_markdown.length} chars)
                      </button>
                    ) : null}
                    {lookupResult.student_sheets?.map((s) => (
                      <button
                        key={s.index}
                        type="button"
                        onClick={() => loadSheet(s.index)}
                        data-testid={`render-preview-load-sheet-${s.index}`}
                        className="block w-full text-left px-2 py-1.5 rounded hover:bg-[#96ded1]/20 text-xs"
                      >
                        <FileText className="h-3 w-3 inline mr-1" />
                        {s.title}{" "}
                        <span className="text-[#4a6984]/60">
                          ({s.sheet_type} · {s.markdown_length} chars)
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>

            <section className="ae-card p-5">
              <h2 className="text-xs font-extrabold uppercase tracking-wide text-[#4a6984] mb-3">
                Render options
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs font-bold">
                  Render as
                  <select
                    value={renderAs}
                    onChange={(e) => setRenderAs(e.target.value)}
                    data-testid="render-preview-render-as"
                    className="mt-1 w-full px-2 py-1.5 rounded border border-[#4a6984]/20 text-sm"
                  >
                    <option value="student_sheet">Student sheet</option>
                    <option value="lesson_plan">Lesson plan (teacher PDF)</option>
                  </select>
                </label>
                {renderAs === "student_sheet" ? (
                  <label className="text-xs font-bold">
                    Sheet type
                    <select
                      value={sheetType}
                      onChange={(e) => setSheetType(e.target.value)}
                      data-testid="render-preview-sheet-type"
                      className="mt-1 w-full px-2 py-1.5 rounded border border-[#4a6984]/20 text-sm"
                    >
                      {SHEET_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <div />
                )}
                <label className="text-xs font-bold">
                  Sheet title
                  <input
                    type="text"
                    value={sheetTitle}
                    onChange={(e) => setSheetTitle(e.target.value)}
                    data-testid="render-preview-sheet-title"
                    className="mt-1 w-full px-2 py-1.5 rounded border border-[#4a6984]/20 text-sm"
                  />
                </label>
                <label className="text-xs font-bold">
                  Activity name
                  <input
                    type="text"
                    value={activityName}
                    onChange={(e) => setActivityName(e.target.value)}
                    data-testid="render-preview-activity-name"
                    className="mt-1 w-full px-2 py-1.5 rounded border border-[#4a6984]/20 text-sm"
                  />
                </label>
                <label className="text-xs font-bold">
                  Grade
                  <input
                    type="text"
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    data-testid="render-preview-grade"
                    className="mt-1 w-full px-2 py-1.5 rounded border border-[#4a6984]/20 text-sm"
                  />
                </label>
              </div>
            </section>

            <section className="ae-card p-5">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xs font-extrabold uppercase tracking-wide text-[#4a6984]">
                  Markdown
                </h2>
                <span className="text-xs text-[#4a6984]/60">{markdown.length} chars</span>
              </div>
              <textarea
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                data-testid="render-preview-markdown"
                rows={22}
                className="w-full px-3 py-2 rounded-md border border-[#4a6984]/20 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
                spellCheck={false}
              />
              <button
                type="button"
                onClick={handleRender}
                disabled={rendering}
                data-testid="render-preview-render-button"
                className="ae-cta mt-3 w-full inline-flex items-center justify-center px-5 py-2.5 rounded-md text-sm font-bold"
              >
                {rendering ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Rendering…
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" /> Render PDF
                  </>
                )}
              </button>
            </section>
          </div>

          {/* Right column — PDF preview / Lint report (tabbed) */}
          <div className="lg:sticky lg:top-6 self-start space-y-3">
            <div className="flex items-center gap-1 ae-card p-1 w-fit">
              <button
                type="button"
                onClick={() => setActiveTab("render")}
                data-testid="render-preview-tab-render"
                className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${
                  activeTab === "render"
                    ? "bg-[#96ded1]/30 text-[#1c2d3d]"
                    : "text-[#4a6984]/70 hover:text-[#1c2d3d]"
                }`}
              >
                <Eye className="h-3.5 w-3.5 inline mr-1" />
                PDF Preview
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("lint")}
                data-testid="render-preview-tab-lint"
                className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${
                  activeTab === "lint"
                    ? "bg-[#96ded1]/30 text-[#1c2d3d]"
                    : "text-[#4a6984]/70 hover:text-[#1c2d3d]"
                }`}
              >
                <Stethoscope className="h-3.5 w-3.5 inline mr-1" />
                Layout Linter
              </button>
            </div>

            {activeTab === "render" ? (
              <section className="ae-card p-2 h-[80vh]" data-testid="render-preview-frame">
                {pdfUrl ? (
                  <iframe
                    src={pdfUrl}
                    title="Rendered PDF preview"
                    className="w-full h-full rounded border border-[#4a6984]/10"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-[#4a6984]/60 text-sm">
                    Render to see the PDF here.
                  </div>
                )}
              </section>
            ) : (
              <section
                className="ae-card p-5 h-[80vh] overflow-y-auto"
                data-testid="render-preview-lint-panel"
              >
                <div className="mb-4">
                  <p className="text-sm text-[#4a6984]/80 mb-3">
                    Pick a lesson on the left, then render it to PDF and
                    scan for mint-card layout anomalies — cards near the
                    110mm cap, pages where mint covers &gt;45% of the
                    page, runaway card counts. Catches &ldquo;big green
                    block&rdquo; regressions before any teacher sees them.
                  </p>
                  <div className="flex flex-wrap items-end gap-2">
                    <label className="text-xs font-bold">
                      Surface
                      <select
                        value={lintSurface}
                        onChange={(e) => setLintSurface(e.target.value)}
                        data-testid="render-preview-lint-surface"
                        className="mt-1 w-full px-2 py-1.5 rounded border border-[#4a6984]/20 text-sm"
                      >
                        <option value="lesson_plan">Lesson plan PDF</option>
                        <option value="student_sheets">Student sheets PDF</option>
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={runLint}
                      disabled={linting || !lookupResult}
                      data-testid="render-preview-run-lint"
                      className="ae-cta inline-flex items-center px-4 py-2 rounded-md text-sm font-bold disabled:opacity-40"
                    >
                      {linting ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                          Linting…
                        </>
                      ) : (
                        <>
                          <Stethoscope className="h-3.5 w-3.5 mr-1.5" />
                          Run Lint
                        </>
                      )}
                    </button>
                  </div>
                  {!lookupResult ? (
                    <p className="mt-3 text-xs text-[#4a6984]/60">
                      No lesson loaded — pick one in the left panel first.
                    </p>
                  ) : null}
                </div>

                {lintReport ? (
                  <>
                    <div
                      className="mb-4 p-3 rounded-md border border-[#4a6984]/10 bg-[#f9fafb]"
                      data-testid="render-preview-lint-summary"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-bold text-sm">{lintReport.activity_name}</div>
                          <div className="text-[10px] text-[#4a6984]/60 uppercase tracking-wide">
                            {lintReport.surface.replace("_", " ")} ·
                            {" "}
                            {lintReport.total_pages} pages ·
                            {" "}
                            {(lintReport.pdf_size_bytes / 1024).toFixed(0)} KB
                          </div>
                        </div>
                        {lintReport.findings?.length === 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                            <CheckCircle2 className="h-3 w-3" /> Clean
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold">
                            <AlertTriangle className="h-3 w-3" />
                            {" "}
                            {lintReport.findings.length} finding{lintReport.findings.length === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-[#4a6984]/70">
                        Caps: cards warn @ {lintReport.cap_warn_mm}mm · hard cap {lintReport.cap_error_mm}mm
                      </div>
                    </div>

                    {lintReport.findings?.length > 0 ? (
                      <div className="space-y-2 mb-5">
                        {lintReport.findings.map((f, i) => {
                          const Icon =
                            f.severity === "error"
                              ? AlertCircle
                              : f.severity === "warning"
                              ? AlertTriangle
                              : Info;
                          const tone =
                            f.severity === "error"
                              ? "border-rose-300 bg-rose-50 text-rose-900"
                              : f.severity === "warning"
                              ? "border-amber-300 bg-amber-50 text-amber-900"
                              : "border-sky-300 bg-sky-50 text-sky-900";
                          return (
                            <div
                              key={i}
                              data-testid={`render-preview-lint-finding-${i}`}
                              className={`flex gap-2 p-2.5 border rounded-md text-xs ${tone}`}
                            >
                              <Icon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <div className="font-bold">
                                  Page {f.page} · {f.code}
                                </div>
                                <div className="mt-0.5 leading-snug">{f.message}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}

                    <div>
                      <div className="text-xs font-extrabold uppercase tracking-wide text-[#4a6984] mb-2">
                        Per-page summary
                      </div>
                      <div className="overflow-x-auto rounded-md border border-[#4a6984]/10">
                        <table className="w-full text-xs">
                          <thead className="bg-[#4a6984]/5 text-[#4a6984]">
                            <tr>
                              <th className="px-2 py-1.5 text-left font-bold">Page</th>
                              <th className="px-2 py-1.5 text-right font-bold">Cards</th>
                              <th className="px-2 py-1.5 text-right font-bold">Total mm</th>
                              <th className="px-2 py-1.5 text-right font-bold">Largest mm</th>
                              <th className="px-2 py-1.5 text-right font-bold">Coverage</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[#4a6984]/10">
                            {(lintReport.pages || []).map((p) => {
                              const cov = p.coverage_pct || 0;
                              const flagged = cov >= 0.45 || p.largest_card_mm >= 95;
                              return (
                                <tr
                                  key={p.page}
                                  className={flagged ? "bg-amber-50/60" : ""}
                                  data-testid={`render-preview-lint-page-${p.page}`}
                                >
                                  <td className="px-2 py-1 font-mono">{p.page}</td>
                                  <td className="px-2 py-1 text-right">{p.mint_card_count}</td>
                                  <td className="px-2 py-1 text-right">{p.mint_total_mm.toFixed(1)}</td>
                                  <td className="px-2 py-1 text-right">{p.largest_card_mm.toFixed(1)}</td>
                                  <td className="px-2 py-1 text-right">{(cov * 100).toFixed(0)}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : !linting ? (
                  <div className="text-[#4a6984]/60 text-sm py-8 text-center">
                    {lookupResult
                      ? "Click Run Lint to scan the rendered PDF."
                      : "Awaiting a lesson selection…"}
                  </div>
                ) : null}
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenderPreview;
